/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './AccountDetailsCostBasisTable.scss';

import { isEmpty } from 'lodash';
import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';

import Content from 'services/Content';
import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';
import GainLossCell from 'components/layouts/Taxes/shared/GainLossCell';
import Optioner from 'components/shared/Optioner/Optioner';
import { Dollar, FormattedDate } from 'components/shared/Text/Text';
import { Quantity } from 'components/shared/Text/Text';

const columns = [
  {
    className: 'column-account',
    columnName: ''
  },
  {
    className: 'column-shares',
    columnName: 'Shares'
  },
  {
    className: 'column-balance',
    columnName: 'Balance'
  },
  {
    className: 'column-cost-basis-method',
    columnName: 'Cost Basis Method'
  },
  {
    className: 'column-cost-per-share',
    columnName: 'Cost Per Share'
  },
  {
    className: 'column-cost-basis-amount',
    columnName: 'Cost Basis Amount'
  },
  {
    className: 'column-total-unrealized-gain-loss',
    columnName: 'Total Unrealized Gain/Loss'
  }
];
let accountTypeName = '';

const AmountCell = ({ amount }) => (
  <td>
    <Quantity>{amount}</Quantity>
  </td>
);

const DollarCell = ({ amount, className }) => (
  <td className={className}>
    <Dollar>{amount}</Dollar>
  </td>
);

const CostBasisMethodCell = ({ method }) => (
  <td>
    <Link className="table-link" to="/profile/cost-basis">
      {method}
    </Link>
  </td>
);

const TableHeaderRow = ({ className, columns }) => (
  <tr className={className}>
    <th className="column-account" colSpan="2">
      {accountTypeName}
    </th>
    {columns.map(({ className, columnName }, idx) => {
      return (
        <th className={className} colSpan={1} key={idx}>
          {columnName}
        </th>
      );
    })}
  </tr>
);

const CostBasisTableSubRowCells = ({ costBasisMethod, data, label, view }) => {
  return columns.map(({ columnName }, key) => (
    <Optioner key={key} val={columnName}>
      {{
        '': (
          <td className="coverage-column-cell" colSpan="3">
            {' '}
            {label}{' '}
          </td>
        ),
        Shares: <AmountCell amount={data.shares} />,
        Balance: <DollarCell amount={data.marketValue} />,
        'Cost Basis Method': (
          <CostBasisMethodCell method={costBasisMethod} view={view} />
        ),
        'Cost Per Share': <DollarCell amount={data.costPerShare} />,
        'Cost Basis Amount': <DollarCell amount={data.costBasisAmount} />,
        'Total Unrealized Gain/Loss': (
          <GainLossCell
            amount={data.gainLossAmount}
            percent={data.gainLossPercent}
            showPercent
          />
        )
      }}
    </Optioner>
  ));
};

const CostBasisAccountRow = ({ balance, view }) => {
  const { costBasisMethod, covered, noncovered, total } = balance;
  return (
    <Fragment>
      <tr className="accounts-cost-basis-table-row total">
        <CostBasisTableSubRowCells
          costBasisMethod={costBasisMethod}
          data={total}
          label="TOTAL"
          view={view}
        />
      </tr>
      <tr className="accounts-cost-basis-table-row">
        <CostBasisTableSubRowCells
          costBasisMethod={costBasisMethod}
          data={covered}
          label="Covered"
          view={view}
        />
      </tr>
      <tr className="accounts-cost-basis-table-row">
        <CostBasisTableSubRowCells
          costBasisMethod={costBasisMethod}
          data={noncovered}
          label="Noncovered"
          view={view}
        />
      </tr>
    </Fragment>
  );
};

const AccountDetailsCostBasisTable = ({
  asOfDate,
  unrealizedCostBasisRows,
  view
}) => {
  const { accountType, rows } = unrealizedCostBasisRows;
  accountTypeName = accountType;

  return (
    !isEmpty(rows) && (
      <div className="accounts-cost-basis-table-container">
        <div>
          <span className="table-header">Cost Basis</span>
          <span className="table-date">
            As of <FormattedDate>{asOfDate}</FormattedDate>
          </span>
        </div>

        <table className="accounts-cost-basis-table">
          <thead>
            <TableHeaderRow className="table-header-row" columns={columns} />
          </thead>
          <tbody>
            <Fragment>
              {rows &&
                rows.map(({ account, balance }, idx) => (
                  <CostBasisAccountRow
                    account={account}
                    balance={balance}
                    key={idx}
                    view={view}
                  />
                ))}
            </Fragment>
          </tbody>
        </table>
        <div className="account-cost-basis-footer">
          <Link
            to={{
              pathname: '/taxes',
              hash: '#cost-basis-table'
            }}
            className="account-realized-loss-gain-button">
            View Realized Gain/Loss
          </Link>
          <DisclosureModal
            content={Content.accountDetailsCostBasisDisclosure}
          />
        </div>
      </div>
    )
  );
};

export default AccountDetailsCostBasisTable;
